import React, { useState, useEffect, useRef } from "react";
import CytoscapeComponent from "react-cytoscapejs";
import {
  initialNodeStyle,
  initialDirectedWeightedEdgeStyle,
} from "./FlowGraphStyle";
import { flowInitialDirectedWeightedEdgeStyle } from "./FlowGraphStyle";
import "./CytoscapeGraph.css";
import EdmonKarps from "./EdmonKarps";
import FordFulkerson from "./FordFulkerson";
import cytoscape from "cytoscape";

const OriginalGraph = ({ nodes, edges }) => {
  const cyStyle = {
    width: "100%",
    height: "100%",
  };

  return (
    <div className="original-graph">
      <h2>Original Graph</h2>
      <div className="cytoscape-container">
        <CytoscapeComponent
          elements={originalNodes.concat(originalEdges)}
          style={cyStyle}
        />
      </div>
    </div>
  );
};

const FlowGraph = ({ algorithmType }) => {
  const [graphGenerated, setGraphGenerated] = useState(false);
  //const [originalGraphGenerated, setOriginalGraphGenerated] = useState(false);
  const [originalGraphVisible, setOriginalGraphVisible] = useState(false);
  const cyStyle = {
    width: "100%",
    height: "100%",
  };

  const resetState = () => {
    setNodes([]);
    setEdges([]);
    setOriginalNodes([]);
    setOriginalEdges([]);
    setVisited([]);
    setQueue([]);
    setAdjacencyList({});
    setGenerateGraph(false);
    setGenerateOriginalGraph(false);
    setEdgeWeights({});
    setSourceNode("S");
    setSinkNode("T");
  };

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [originalNodes, setOriginalNodes] = useState([]);
  const [originalEdges, setOriginalEdges] = useState([]);
  const [visited, setVisited] = useState([]);
  const [queue, setQueue] = useState([]);
  const [cytoscapeReady, setCytoscapeReady] = useState(false);

  const [numNodes, setNumNodes] = useState(2); // Number of nodes
  const [adjacencyList, setAdjacencyList] = useState({}); // Adjacency list
  const [generateGraph, setGenerateGraph] = useState(false);
  const [generateOriginalGraph, setGenerateOriginalGraph] = useState(false);
  const [edgeWeights, setEdgeWeights] = useState({});
  const [selectedStartingNode, setSelectedStartingNode] = useState("S");
  const [sourceNode, setSourceNode] = useState("S");
  const [sinkNode, setSinkNode] = useState("T");

  // Create a ref to store the Cytoscape instance
  const cytoscapeRef = useRef(null);

  useEffect(() => {
    if (generateGraph) {
      const generatedElements = generateGraphElements(numNodes, adjacencyList);
      setNodes(generatedElements.nodes);
      setEdges(generatedElements.edges);
      if (cytoscapeRef.current) {
        cytoscapeRef.current.fit();
      }
    }
  }, [generateGraph, numNodes, adjacencyList]);

  const generateGraphElements = (numNodes, adjacencyList) => {
    const generatedNodes = [];
    const generatedEdges = [];

    // Calculate the number of rows and columns for the grid
    const numRows = Math.ceil(Math.sqrt(numNodes));
    const numCols = Math.ceil(numNodes / numRows);

    // Calculate the spacing between nodes
    const spacingX = 300 / numCols;
    const spacingY = 300 / numRows;

    // Generate nodes in a grid
    for (let row = 0; row < numRows; row++) {
      for (let col = 0; col < numCols; col++) {
        const nodeId = row * numCols + col + 1;
        if (nodeId <= numNodes) {
          generatedNodes.push({
            data: { id: `node${nodeId}`, label: `${nodeId}` },
            position: { x: col * spacingX, y: row * spacingY },
          });
        }
      }
    }

    // Add source node 'S' to generated nodes
    generatedNodes.push({
      data: { id: "S", label: "S" }, // Add source node 'S'
      position: { x: -100, y: 0 }, // Position it to the left of the graph
    });

    // Add sink node 'T' to generated nodes
    generatedNodes.push({
      data: { id: "T", label: "T" }, // Add sink node 'T'
      position: { x: numCols * spacingX + 100, y: (numRows - 1) * spacingY }, // Position it to the right of the graph
    });

    for (const sourceNode in adjacencyList) {
      const targetNodes = adjacencyList[sourceNode];
      targetNodes.forEach((targetNode) => {
        const edgeId = `${sourceNode}-${targetNode}`;
        const backEdgeId = `${targetNode}-${sourceNode}`;
        const weight = edgeWeights[edgeId] || "";

        // Check if the target node is the sink node 'T'
        const targetNodeId = targetNode === "T" ? "T" : `node${targetNode}`;
        const sourceNodeId = sourceNode === "S" ? "S" : `node${sourceNode}`;
        generatedEdges.push({
          data: {
            id: edgeId,
            source: sourceNodeId,
            target: targetNodeId,
            weight: weight,
          },
        });
        generatedEdges.push({
          data: {
            id: backEdgeId,
            source: targetNodeId,
            target: sourceNodeId,
            weight: 0,
          },
        });
      });
    }

    return { nodes: generatedNodes, edges: generatedEdges };
  };
  const generateOriginalGraphElements = (numNodes, adjacencyList) => {
    const generatedNodes = [];
    const generatedEdges = [];

    // Calculate the number of rows and columns for the grid
    const numRows = Math.ceil(Math.sqrt(numNodes));
    const numCols = Math.ceil(numNodes / numRows);

    // Calculate the spacing between nodes
    const spacingX = 300 / numCols;
    const spacingY = 300 / numRows;

    // Generate nodes in a grid
    for (let row = 0; row < numRows; row++) {
      for (let col = 0; col < numCols; col++) {
        const nodeId = row * numCols + col + 1;
        if (nodeId <= numNodes) {
          generatedNodes.push({
            data: { id: `node${nodeId}`, label: `${nodeId}` },
            position: { x: col * spacingX + 100, y: row * spacingY + 70 },
          });
        }
      }
    }

    // Add source node 'S' to generated nodes
    generatedNodes.push({
      data: { id: "S", label: "S" }, // Add source node 'S'
      position: { x: 20, y: 140 }, // Position it to the left of the graph
    });

    // Add sink node 'T' to generated nodes
    generatedNodes.push({
      data: { id: "T", label: "T" }, // Add sink node 'T'
      position: { x: numCols * spacingX + 40, y: (numRows - 1) * spacingY }, // Position it to the right of the graph
    });

    for (const sourceNode in adjacencyList) {
      const targetNodes = adjacencyList[sourceNode];
      targetNodes.forEach((targetNode) => {
        const edgeId = `${sourceNode}-${targetNode}`;
        //const backEdgeId = `${targetNode}-${sourceNode}`;
        const weight = edgeWeights[edgeId] || "";

        // Check if the target node is the sink node 'T'
        const targetNodeId = targetNode === "T" ? "T" : `node${targetNode}`;
        const sourceNodeId = sourceNode === "S" ? "S" : `node${sourceNode}`;
        generatedEdges.push({
          data: {
            id: edgeId,
            source: sourceNodeId,
            target: targetNodeId,
            weight: weight,
          },
        });
      });
    }

    return { nodes: generatedNodes, edges: generatedEdges };
  };

  const handleNumNodesChange = (e) => {
    const newNumNodes = parseInt(e.target.value);

    if (newNumNodes >= 2 && newNumNodes <= 10) {
      setNumNodes(newNumNodes);
      if (selectedStartingNode > newNumNodes) {
        setSelectedStartingNode(newNumNodes);
      }
    } else if (newNumNodes < 2) {
      setNumNodes(2); // Minimum value of 2
    } else if (newNumNodes > 5) {
      setNumNodes(5); // Maximum value of 5
    }
  };

  const handleAdjacencyChange = (node, adjacentNode) => {
    const updatedAdjacencyList = { ...adjacencyList };

    if (!updatedAdjacencyList[node]) {
      updatedAdjacencyList[node] = [];
    }

    const nodeIndex = updatedAdjacencyList[node].indexOf(adjacentNode);

    if (nodeIndex !== -1) {
      // If the edge (node -> adjacentNode) exists, remove it
      updatedAdjacencyList[node].splice(nodeIndex, 1);
    } else {
      // If the edge (node -> adjacentNode) doesn't exist, add it
      updatedAdjacencyList[node].push(adjacentNode);
    }

    setAdjacencyList(updatedAdjacencyList);
  };

  const allEdgesWeighted = () => {
    for (const sourceNode in adjacencyList) {
      const targetNodes = adjacencyList[sourceNode];
      for (const targetNode of targetNodes) {
        const edgeId = `${sourceNode}-${targetNode}`;
        const reversedEdgeId = `${targetNode}-${sourceNode}`;

        // Check if the weight has been entered for either direction
        if (!edgeWeights[edgeId] && !edgeWeights[reversedEdgeId]) {
          return false;
        }
      }
    }
    return true;
  };

  const handleEdgeWeightChange = (edgeId, weight) => {
    //const numericWeight = parseInt(weight);
    if (isNaN(weight) || weight < 0) {
      //console.log("Invalid weight:", weight);
      alert("Please enter a non-negative numeric weight.");
      return;
    }
    setEdgeWeights((prevEdgeWeights) => {
      // Update the edge weight for the provided edge
      const newEdgeWeights = { ...prevEdgeWeights };
      newEdgeWeights[edgeId] = weight;

      // Split the edgeId into source and target nodes
      const [sourceNode, targetNode] = edgeId.split("-");

      // Create a copy of the adjacency list
      const updatedAdjacencyList = { ...adjacencyList };

      // Update the weights for one direction
      newEdgeWeights[`${sourceNode}-${targetNode}`] = weight;

      setAdjacencyList(updatedAdjacencyList);
      return newEdgeWeights;
    });
  };

  const handleGenerateGraph = () => {
    setNodes([]);
    setEdges([]);
    setOriginalNodes([]);
    setOriginalEdges([]);
    if (!allEdgesWeighted()) {
      alert("Please enter weights for all edges before generating the graph.");
      return;
    }
    if (selectedStartingNode > numNodes) {
      setSelectedStartingNode(numNodes);
    }
    setGenerateGraph((prevGenerateGraph) => {
      if (!prevGenerateGraph) {
        setNodes([]);
        setEdges([]);
        setOriginalNodes([]);
        setOriginalEdges([]);

        const generatedElements = generateGraphElements(
          numNodes,
          adjacencyList
        );
        const generatedOriginalElements = generateOriginalGraphElements(
          numNodes,
          adjacencyList
        );
        setNodes(generatedElements.nodes);
        setEdges(generatedElements.edges);
        setOriginalNodes(generatedOriginalElements.nodes);
        setOriginalEdges(generatedOriginalElements.edges);
      }
      return !prevGenerateGraph;
    });
    setGraphGenerated(true);

    setOriginalGraphVisible(true);
  };

  const handleCytoscapeReady = (cy) => {
    if (cy) {
      cy.style()
        .fromJson([
          {
            selector: "node",
            style: {
              ...initialNodeStyle,
            },
          },
          {
            selector: "edge",
            style: {
              ...initialDirectedWeightedEdgeStyle,
            },
          },
        ])
        .update();

      setCytoscapeReady(true);
    }
  };

  const handleOriginalCytoscapeReady = (cy) => {
    if (cy) {
      cy.style()
        .fromJson([
          {
            selector: "node",
            style: {
              ...initialNodeStyle,
            },
          },
          {
            selector: "edge",
            style: {
              ...flowInitialDirectedWeightedEdgeStyle,
            },
          },
        ])
        .update();

      setCytoscapeReady(true);
    }
  };

  return (
    <div className="graph-container">
      <div className="left-column">
        <div className="graph-options">
          {algorithmType === "edmonKarps" && (
            <label className="graph-option">
              <b>Go ahead and create a Network Flow Graph.</b>
            </label>
          )}
          {algorithmType === "fordfulkerson" && (
            <label className="graph-option">
              <b>Go ahead and create a Network Flow Graph.</b>
            </label>
          )}
        </div>
        <div className="node-input">
          <label className="node-label">
            Number of Nodes (not including source and sink):
          </label>
          <input
            className="node-input-field"
            type="number"
            value={numNodes}
            onChange={handleNumNodesChange}
            min="2"
            max="5"
          />
        </div>
        <div className="node-input">
          <label className="node-label">Source Node:</label>
          <input
            className="node-input-field"
            type="text"
            value={sourceNode}
            onChange={(e) => setSourceNode(e.target.value)}
            disabled={true}
          />
        </div>
        <div className="node-input">
          <label className="node-label">Sink Node:</label>
          <input
            className="node-input-field"
            type="text"
            value={sinkNode}
            onChange={(e) => setSinkNode(e.target.value)}
            disabled={true}
          />
        </div>
        <div className="adjacency-list">
          <label className="adjacency-label">Adjacency List:</label>
          <ul className="adjacency-list-ul">
            <li key="s">
              <b>Node S:</b>&nbsp;&nbsp;&nbsp;
              {Array.from({ length: numNodes }, (_, i) => i + 1).map(
                (adjNode) => (
                  <div key={`s-${adjNode}`} className="adjacency-list-entry">
                    <input
                      type="checkbox"
                      checked={(adjacencyList["S"] || []).includes(adjNode)}
                      onChange={() => handleAdjacencyChange("S", adjNode)}
                    />
                    {adjNode}
                    &nbsp;&nbsp;&nbsp;
                    <input
                      type="number"
                      placeholder="Weight"
                      value={edgeWeights[`S-${adjNode}`] || ""}
                      onChange={(e) =>
                        handleEdgeWeightChange(`S-${adjNode}`, e.target.value)
                      }
                      style={{ width: "69px" }}
                      disabled={!adjacencyList["S"]?.includes(adjNode)}
                    />
                  </div>
                )
              )}
            </li>

            {Array.from({ length: numNodes }, (_, i) => i + 1).map((node) => (
              <li key={node}>
                <b>Node {node}:</b>&nbsp;&nbsp;&nbsp;
                {Array.from({ length: numNodes }, (_, i) => i + 1)
                  .filter((adjNode) => adjNode !== node)
                  .map((adjNode) => (
                    <label key={adjNode} className="adjacency-list-entry">
                      <input
                        type="checkbox"
                        checked={(adjacencyList[node] || []).includes(adjNode)}
                        onChange={() => handleAdjacencyChange(node, adjNode)}
                      />
                      {adjNode}
                      &nbsp;&nbsp;&nbsp;
                      <input
                        type="number"
                        placeholder="Weight"
                        value={edgeWeights[`${node}-${adjNode}`] || ""}
                        onChange={(e) =>
                          handleEdgeWeightChange(
                            `${node}-${adjNode}`,
                            e.target.value
                          )
                        }
                        style={{ width: "69px" }}
                        disabled={!adjacencyList[node]?.includes(adjNode)}
                      />
                    </label>
                  ))}
                <div className="adjacency-list-entry">
                  <input
                    type="checkbox"
                    checked={(adjacencyList[node] || []).includes("T")}
                    onChange={() => handleAdjacencyChange(node, "T")}
                  />
                  T &nbsp;&nbsp;&nbsp;
                  <input
                    type="number"
                    placeholder="Weight"
                    value={edgeWeights[`${node}-T`] || ""}
                    onChange={(e) =>
                      handleEdgeWeightChange(`${node}-T`, e.target.value)
                    }
                    style={{ width: "69px" }}
                    disabled={!adjacencyList[node]?.includes("T")}
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="graph-controls">
          <button
            className="generate-reset-button"
            onClick={handleGenerateGraph}
          >
            Generate Graph
          </button>
          &nbsp;&nbsp;
          <button className="generate-reset-button" onClick={resetState}>
            Reset Graph
          </button>
        </div>
        <div className="cytoscape-original-container ">
          <label className="adjacency-label">Original Graph:</label>
          {originalGraphVisible && (
            <CytoscapeComponent
              elements={originalNodes.concat(originalEdges)}
              style={cyStyle}
              cy={(cy) => {
                cy.userZoomingEnabled(false);
                cytoscapeRef.current = cy;
                handleOriginalCytoscapeReady(cy);
              }}
            />
          )}
        </div>
      </div>
      <div className="right-column">
        <div className="cytoscape-container ">
          <label className="adjacency-label">Residual Graph:</label>
          <CytoscapeComponent
            elements={nodes.concat(edges)}
            style={cyStyle}
            cy={(cy) => {
              cy.userZoomingEnabled(false);
              cytoscapeRef.current = cy;
              handleCytoscapeReady(cy);
            }}
          />
        </div>

        {graphGenerated && algorithmType === "edmonKarps" && (
          <EdmonKarps
            cytoscape={cytoscapeRef.current}
            nodes={nodes}
            visited={visited}
            queue={queue}
            setVisited={setVisited}
            setQueue={setQueue}
            sourceNode={sourceNode}
            sinkNode={sinkNode}
            adjacencyList={adjacencyList}
            edgeWeights={edgeWeights}
            generateGraphElements={generateGraphElements}
            numNodes={numNodes}
          />
        )}
        {graphGenerated && algorithmType === "fordfulkerson" && (
          <FordFulkerson
            cytoscape={cytoscapeRef.current}
            nodes={nodes}
            visited={visited}
            queue={queue}
            setVisited={setVisited}
            setQueue={setQueue}
            sourceNode={sourceNode}
            sinkNode={sinkNode}
            adjacencyList={adjacencyList}
            edgeWeights={edgeWeights}
            generateGraphElements={generateGraphElements}
            numNodes={numNodes}
          />
        )}
      </div>
    </div>
  );
};

export default FlowGraph;
